import { useCallback, useEffect, useMemo } from 'react'
import { compact, isUndefined } from 'lodash'

import { ChartItem, TableDataItem } from 'types'

export type UseChartTableSelectedRowsProps = {
  selectedCharts: string[]
  data?: TableDataItem[]
  charts?: ChartItem[]
  onSelectCharts: (newSelectedCharts: string[]) => void
}

const getSelectedRowIds = (
  data: TableDataItem[],
  selectedCharts: string[],
  parentIndex?: number,
) => {
  return data.reduce<Record<string, boolean>>((acc, curr, index) => {
    if (curr.subRows?.length) {
      acc = {
        ...acc,
        ...getSelectedRowIds(curr.subRows, selectedCharts, index),
      }
    }

    if (curr.value && selectedCharts?.includes(curr.value)) {
      if (!isUndefined(parentIndex)) {
        acc[`${parentIndex}.${index}`] = true
      } else {
        acc[index] = true
      }
    }

    return acc
  }, {})
}
const getNewRowIds = ({
  data,
  compositeId,
  value,
  currentRowIds,
  additionalDataIndex,
}: {
  data: TableDataItem[]
  compositeId: string
  value: boolean
  currentRowIds: Record<string, boolean>
  additionalDataIndex?: number
}): Record<string, boolean> => {
  const newRowIds = { ...currentRowIds }

  const [id, subId] = compositeId.split('.')

  newRowIds[compositeId] = value

  if (!subId && !isUndefined(additionalDataIndex) && additionalDataIndex !== -1) {
    data[id]?.subRows?.forEach((_, index) => {
      newRowIds[`${id}.${index}`] = value
    })

    const additionalChartIndex = data[additionalDataIndex].subRows?.findIndex(
      item => item.name === data[id].name,
    )

    if (!isUndefined(additionalChartIndex) && additionalChartIndex !== -1) {
      newRowIds[`${additionalDataIndex}.${additionalChartIndex}`] = value
    }
  }

  return newRowIds
}

export const useChartTableSelectedRows = ({
  data,
  selectedCharts,
  charts,
  onSelectCharts,
}: UseChartTableSelectedRowsProps) => {
  const additionalDataIndex = data?.findIndex(item => !!item.subRows)
  const selectedRowIds = useMemo(() => {
    if (!selectedCharts || !data) {
      return {}
    }

    return getSelectedRowIds(data, selectedCharts)
  }, [selectedCharts, data])

  useEffect(() => {
    const chartsNames = charts?.map(chart => chart.value)
    const newSelectedCharts = selectedCharts?.filter(chart =>
      chartsNames?.includes(chart),
    )
    if (newSelectedCharts?.length) {
      return
    }

    handleRowSelect('0', true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, additionalDataIndex])

  const handleRowSelect = useCallback(
    (compositeId: string | string[], value: boolean) => {
      if (!data?.length) {
        return
      }

      let newSelectedRowIds: Record<string, boolean>

      const commonArgs = {
        data,
        value,
        additionalDataIndex,
      }

      if (typeof compositeId === 'string') {
        newSelectedRowIds = getNewRowIds({
          ...commonArgs,
          compositeId,
          currentRowIds: selectedRowIds,
        })
      } else {
        newSelectedRowIds = compositeId.reduce(
          (currentIds: Record<string, boolean>, singleCompositeId) =>
            getNewRowIds({
              ...commonArgs,
              compositeId: singleCompositeId,
              currentRowIds: currentIds,
            }),
          selectedRowIds,
        )
      }

      onSelectCharts(
        compact(
          Object.keys(newSelectedRowIds)
            .filter(key => newSelectedRowIds[key])
            .map(rowIndex => {
              const [key, nestedKey] = rowIndex.split('.')

              return isUndefined(nestedKey)
                ? data[key]?.value
                : data[key]?.subRows?.[nestedKey]?.value
            }),
        ),
      )
    },
    [data, selectedRowIds, onSelectCharts, additionalDataIndex],
  )

  return {
    selectedRowIds,
    onSelect: handleRowSelect,
  }
}
